import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 28px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  font-size: 24px;
  padding: 4px;
  line-height: 1;
  
  &:hover {
    color: #64748b;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const TipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TipRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TipInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #2DBCAF;
  }
`;

const TipBtn = styled.button<{ $danger?: boolean }>`
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  background: ${p => p.$danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(45, 188, 175, 0.1)'};
  color: ${p => p.$danger ? '#dc2626' : '#2DBCAF'};
  
  &:hover {
    background: ${p => p.$danger ? 'rgba(239, 68, 68, 0.2)' : 'rgba(45, 188, 175, 0.2)'};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => p.$primary ? `
    background: #2DBCAF;
    color: #fff;
    border: none;
    
    &:hover:not(:disabled) {
      background: #26a89d;
    }
  ` : `
    background: #fff;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover:not(:disabled) {
      background: #f8fafc;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMsg = styled.div`
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

const ErrorMsg = styled.div`
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

interface TKContent {
  _id: string;
  featureSlug: string;
  ageGroup?: string;
  category?: string;
  title: string;
  description?: string;
  order: number;
  isActive: boolean;
  metadata?: {
    energyKcal?: number;
    texture?: string;
    frequency?: string;
    portionStart?: string;
    portionTarget?: string;
    tips?: string[];
  };
}

interface Props {
  item: TKContent;
  onClose: () => void;
  onSaved: (updatedItem: TKContent) => void;
}

const EditMPASIModal: React.FC<Props> = ({ item, onClose, onSaved }) => {
  const [title, setTitle] = useState(item.title || '');
  const [description, setDescription] = useState(item.description || '');
  const [energyKcal, setEnergyKcal] = useState(String(item.metadata?.energyKcal || ''));
  const [texture, setTexture] = useState(item.metadata?.texture || '');
  const [frequency, setFrequency] = useState(item.metadata?.frequency || '');
  const [portionStart, setPortionStart] = useState(item.metadata?.portionStart || '');
  const [portionTarget, setPortionTarget] = useState(item.metadata?.portionTarget || '');
  const [tips, setTips] = useState<string[]>(item.metadata?.tips || []);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleAddTip = () => {
    setTips([...tips, '']);
  };

  const handleRemoveTip = (index: number) => {
    setTips(tips.filter((_, i) => i !== index));
  };

  const handleTipChange = (index: number, value: string) => {
    const newTips = [...tips];
    newTips[index] = value;
    setTips(newTips);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title wajib diisi');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`/api/admin/tumbuh-kembang/content/${item._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          metadata: {
            energyKcal: energyKcal ? Number(energyKcal) : undefined,
            texture: texture.trim(),
            frequency: frequency.trim(),
            portionStart: portionStart.trim(),
            portionTarget: portionTarget.trim(),
            tips: tips.filter(t => t.trim()),
          },
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setSuccess(true);
        setTimeout(() => {
          onSaved(data.content);
        }, 500);
      } else {
        const data = await res.json();
        setError(data.error || 'Gagal menyimpan');
      }
    } catch {
      setError('Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>🍽️ Edit Panduan MPASI</Title>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        {success && <SuccessMsg>✓ Berhasil disimpan!</SuccessMsg>}
        {error && <ErrorMsg>{error}</ErrorMsg>}

        <FormGroup>
          <Label>Kelompok Usia</Label>
          <Input value={item.ageGroup || ''} disabled style={{ background: '#f8fafc' }} />
        </FormGroup>

        <FormGroup>
          <Label>Title *</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="6-8 Bulan"
          />
        </FormGroup>

        <FormGroup>
          <Label>Deskripsi</Label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Pengenalan MPASI pertama..."
          />
        </FormGroup>

        <Row>
          <FormGroup>
            <Label>Energi (Kcal)</Label>
            <Input
              type="number"
              value={energyKcal}
              onChange={(e) => setEnergyKcal(e.target.value)}
              placeholder="200"
            />
          </FormGroup>

          <FormGroup>
            <Label>Frekuensi</Label>
            <Input
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              placeholder="2-3 kali makan utama..."
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Tekstur Makanan</Label>
          <Input
            value={texture}
            onChange={(e) => setTexture(e.target.value)}
            placeholder="Bubur kental, makanan yang dilumatkan"
          />
        </FormGroup>

        <Row>
          <FormGroup>
            <Label>Porsi Awal</Label>
            <Input
              value={portionStart}
              onChange={(e) => setPortionStart(e.target.value)}
              placeholder="2-3 sendok makan"
            />
          </FormGroup>

          <FormGroup>
            <Label>Porsi Target</Label>
            <Input
              value={portionTarget}
              onChange={(e) => setPortionTarget(e.target.value)}
              placeholder="½ mangkok (125 ml)"
            />
          </FormGroup>
        </Row>

        <FormGroup>
          <Label>Tips</Label>
          <TipsContainer>
            {tips.map((tip, index) => (
              <TipRow key={index}>
                <TipInput
                  value={tip}
                  onChange={(e) => handleTipChange(index, e.target.value)}
                  placeholder={`Tip ${index + 1}...`}
                />
                <TipBtn $danger onClick={() => handleRemoveTip(index)}>✕</TipBtn>
              </TipRow>
            ))}
            <TipBtn onClick={handleAddTip}>+ Tambah Tip</TipBtn>
          </TipsContainer>
        </FormGroup>

        <Actions>
          <Button onClick={onClose} disabled={loading}>Batal</Button>
          <Button $primary onClick={handleSave} disabled={loading}>
            {loading ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </Actions>
      </Modal>
    </Overlay>
  );
};

export default EditMPASIModal;
